import { useEffect, useRef, useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { LANGUAGES } from '@/i18n';
import { useLanguage } from '@/i18n/LanguageContext';

export default function LanguageSwitcher({ scrolled = false }) {
  const { locale, setLocale, content } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((lang) => lang.code === locale) ?? LANGUAGES[1];

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buttonClass = scrolled
    ? 'border-[#E2E8F0] text-[#0D1B2A]/80 hover:bg-[#F1F5F9]'
    : 'border-white/25 text-white/90 hover:bg-white/10';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={content.a11y.chooseLanguage}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${buttonClass}`}
      >
        <Globe size={14} className={scrolled ? 'text-[#2563EB]' : 'text-[#22D3EE]'} />
        <span>{current.short}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={content.a11y.chooseLanguage}
          className="absolute right-0 z-50 mt-2 min-w-[10rem] overflow-hidden rounded-xl border border-[#E2E8F0] bg-white py-1 shadow-lg"
        >
          {LANGUAGES.map((lang) => {
            const selected = lang.code === locale;
            return (
              <li key={lang.code} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    setLocale(lang.code);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-[#F1F5F9] ${
                    selected ? 'text-[#2563EB]' : 'text-[#0D1B2A]'
                  }`}
                >
                  <span>
                    <span className="font-semibold">{lang.short}</span>
                    <span className="ml-2 text-[#64748B]">{lang.label}</span>
                  </span>
                  {selected && <Check size={14} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
